import { memo, useEffect, useMemo } from "react"
import { useParams } from "@tanstack/react-router"
import { IconPlusOutline18 as Plus } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { IconButton, useBreakpoints } from "@productlane/ui"

import type { HiddenTier } from "./hidden-tier-folders"
import type { GroupWithArticles } from "./types"
import type { DocsSectionId } from "@/stores/docs/docs-nav-store"
import { NavSectionHeader } from "@/components/shared/nav-section-header"
import { RoleCheck } from "@/components/shared/role-check"
import {
	DOCS_SECTIONS,
	useDocNavStore,
	useIsDocsSectionOpen,
} from "@/stores/docs/docs-nav-store"
import { DocsNavPlaceholderRow } from "./docs-nav-placeholder-row"
import { useHelpCenterArticlesSettled } from "./help-center-context"
import { HiddenFolderRow } from "./hidden-folder-row"
import {
	collectHiddenFolders,
	folderHoldsArticle,
	hiddenFolderKey,
} from "./hidden-tier-folders"
import { useCreateArticle } from "./use-create-article"
import { useDelayedUnmount } from "./use-delayed-unmount"

// Hidden articles live here instead of inside the public groups, so an article
// never appears twice in the tree. They keep the folder they sit in, rendered
// the same way the public tree renders a group.
const HiddenArticlesSection = memo(
	({
		articleGroups,
		tier,
		label,
		sectionId,
		addLabel,
		// The Agent section is where a first hidden article gets written, so it
		// holds its ground with a placeholder. Internal appears once it has rows.
		showWhenEmpty,
	}: {
		articleGroups: readonly GroupWithArticles[]
		tier: HiddenTier
		label: string
		sectionId: DocsSectionId
		addLabel: string
		showWhenEmpty: boolean
	}) => {
		const createArticle = useCreateArticle({ articleGroups })
		const articlesSettled = useHelpCenterArticlesSettled()
		const isOpen = useIsDocsSectionOpen(sectionId)
		const toggleSection = useDocNavStore((s) => s.toggleSection)
		const renderRows = useDelayedUnmount(isOpen, 250)
		const params = useParams({ strict: false })

		const folders = useMemo(
			() => collectHiddenFolders(articleGroups, tier),
			[articleGroups, tier],
		)

		// Open the folder holding the selected article, so a row reached by j/k or
		// by search is on screen. A folder the user closed by hand stays closed
		// unless the arrow keys are what moved the selection.
		useEffect(() => {
			if (!params.id) return
			const folder = folders.find((f) => folderHoldsArticle(f, params.id ?? ""))
			if (!folder) return
			const key = hiddenFolderKey(tier, folder.group.id)
			const store = useDocNavStore.getState()
			if (store.openGroupIds.includes(key)) return
			if (
				store.manuallyClosedGroups.includes(key) &&
				!store.isArrowNavigation
			) {
				return
			}
			store.openGroup(key)
		}, [params.id, folders, tier])

		if (folders.length === 0 && !showWhenEmpty) return null

		return (
			<li className="group/docs-section mt-5">
				<NavSectionHeader
					label={label}
					isOpen={isOpen}
					onToggle={() => toggleSection(sectionId)}
					action={
						<AddHiddenArticleButton
							articleGroups={articleGroups}
							tier={tier}
							label={addLabel}
						/>
					}
					// These rows are siblings of the header, so unlike Public the section
					// inherits no list gap under the label.
					className="pb-1"
				/>
				<div
					inert={!isOpen}
					className={cn(
						"ease-[cubic-bezier(0.23,1,0.32,1)] grid transition-[grid-template-rows,opacity] duration-200",
						isOpen
							? "grid-rows-[1fr] opacity-100"
							: "grid-rows-[0fr] opacity-40",
					)}
				>
					<div className="min-h-0 overflow-hidden">
						<ul className="flex list-none flex-col gap-px pb-1">
							{renderRows && (
								<>
									{folders.length === 0 && articlesSettled && (
										<RoleCheck not={["VIEWER"]}>
											<li>
												<DocsNavPlaceholderRow
													label={addLabel}
													onClick={() => createArticle({ visibility: tier })}
												/>
											</li>
										</RoleCheck>
									)}
									{folders.map((folder) => (
										<HiddenFolderRow
											key={folder.group.id}
											folder={folder}
											tier={tier}
											articleGroups={articleGroups}
											addLabel={addLabel}
										/>
									))}
								</>
							)}
						</ul>
					</div>
				</div>
			</li>
		)
	},
)

HiddenArticlesSection.displayName = "HiddenArticlesSection"

export const AgentArticlesSection = ({
	articleGroups,
}: {
	articleGroups: readonly GroupWithArticles[]
}) => (
	<HiddenArticlesSection
		articleGroups={articleGroups}
		tier="UNLISTED"
		label="Agent"
		sectionId={DOCS_SECTIONS.agent}
		addLabel="New agent article"
		showWhenEmpty
	/>
)

export const InternalArticlesSection = ({
	articleGroups,
}: {
	articleGroups: readonly GroupWithArticles[]
}) => (
	<HiddenArticlesSection
		articleGroups={articleGroups}
		tier="INTERNAL"
		label="Internal"
		sectionId={DOCS_SECTIONS.internal}
		addLabel="New internal article"
		showWhenEmpty={false}
	/>
)

export const AddHiddenArticleButton = memo(
	({
		articleGroups,
		tier,
		label,
	}: {
		articleGroups: readonly GroupWithArticles[]
		tier: HiddenTier
		label: string
	}) => {
		const createArticle = useCreateArticle({ articleGroups })
		const { isMobile } = useBreakpoints()

		return (
			<RoleCheck not={["VIEWER"]}>
				<IconButton
					Icon={Plus}
					variant="ghost"
					size="sm"
					aria-label={label}
					onClick={() => createArticle({ visibility: tier })}
					className={cn(
						"flex-none text-tertiary hover:text-primary focus-visible:opacity-100",
						isMobile
							? "opacity-100"
							: "opacity-0 group-hover/section:opacity-100",
					)}
				/>
			</RoleCheck>
		)
	},
)

AddHiddenArticleButton.displayName = "AddHiddenArticleButton"
